/**
 * Dictionary Service: the spell check dictionary on the Larry backend.
 *
 * - GET  /dictionary             custom words + student roster (both layers or an error)
 * - POST /dictionary/words/add     409 `DICTIONARY_WORD_DUPLICATE` when the word already exists
 * - POST /dictionary/words/remove  removing a missing word is not an error
 * - POST /dictionary/clear        removes every custom word in one request
 *
 * The dictionary belongs to the book's school, not to a spread, so no call sends a `spreadId`.
 * `GET /dictionary` also registers this connection for the book's live `DictionaryChangedEvent`
 * for 2 hours, which is why it is read again after a WebSocket reconnect (`useDictionarySync`).
 */

import { httpClient } from '@/api/http/client'
import type {
  DictionaryClearResult,
  DictionaryResponse,
  DictionaryWordRequest,
  DictionaryWordResult,
} from '@/api/types/dictionary'

export const dictionaryService = {
  getDictionary(): Promise<DictionaryResponse> {
    return httpClient.get<DictionaryResponse>('/dictionary')
  },

  addWord(word: string): Promise<DictionaryWordResult> {
    return httpClient.post<DictionaryWordRequest, DictionaryWordResult>('/dictionary/words/add', {
      word,
    })
  },

  removeWord(word: string): Promise<DictionaryWordResult> {
    return httpClient.post<DictionaryWordRequest, DictionaryWordResult>(
      '/dictionary/words/remove',
      { word }
    )
  },

  clearWords(): Promise<DictionaryClearResult> {
    return httpClient.post<Record<string, never>, DictionaryClearResult>('/dictionary/clear', {})
  },
}
